import React, { useState } from 'react';
import { ChevronDown, ChevronRight } from 'lucide-react';
import { FieldSchema } from '../../types';

interface FieldGroupProps {
  field: FieldSchema;
  children: React.ReactNode;
  hasErrors?: boolean;
  hasOverrides?: boolean;
  /** Label for the amber badge when hasOverrides is set. */
  overrideLabel?: string;
}

export const FieldGroup: React.FC<FieldGroupProps> = ({
  field,
  children,
  hasErrors = false,
  hasOverrides = false,
  overrideLabel = 'Overridden'
}) => {
  const [isCollapsed, setIsCollapsed] = useState(field.defaultCollapsed ?? false);
  const isCollapsible = field.collapsible !== false;

  const getBorderClass = () => {
    if (hasErrors && !hasOverrides) return 'border-red-200 bg-red-50/30';
    if (hasErrors && hasOverrides) return 'border-amber-200 bg-amber-50/30';
    return 'border-slate-200 bg-slate-50/30';
  };

  const handleToggle = () => {
    if (isCollapsible) {
      setIsCollapsed(!isCollapsed);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      handleToggle();
    }
  };

  return (
    <div className={`mb-4 rounded-lg border ${getBorderClass()}`}>
      <div
        className={`flex items-center justify-between px-4 py-3 ${
          isCollapsible ? 'cursor-pointer hover:bg-slate-100/50' : ''
        } transition-colors rounded-t-lg border-b border-slate-200`}
        {...(isCollapsible ? {
          role: 'button' as const,
          tabIndex: 0,
          'aria-expanded': !isCollapsed,
          onClick: handleToggle,
          onKeyDown: handleKeyDown,
        } : {})}
      >
        <div className="flex items-center space-x-2">
          {isCollapsible && (
            isCollapsed
              ? <ChevronRight className="w-4 h-4 text-slate-400" />
              : <ChevronDown className="w-4 h-4 text-slate-400" />
          )}
          <span className="text-sm font-semibold text-slate-700 uppercase tracking-wide">
            {field.label}
          </span>
          {hasErrors && (
            <span className={`text-xs px-2 py-0.5 rounded-full ${
              hasOverrides ? 'bg-amber-100 text-amber-700' : 'bg-red-100 text-red-700'
            }`}>
              {hasOverrides ? overrideLabel : 'Has Errors'}
            </span>
          )}
        </div>
        {field.hint && (
          <span className="text-xs text-slate-400">{field.hint}</span>
        )}
      </div>

      {!isCollapsed && (
        <div className="p-4 pl-6">
          {children}
        </div>
      )}
    </div>
  );
};
